import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { api, globalErrorHandler } from '../../api/create-api';
import { Row, Col, Button, InputGroup, ListGroupItem, Label } from 'reactstrap';
import { Prompt } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronLeft, faCopy, faMinusCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { Field } from '@galop/component-library';
import { CancelButton, SelectCollection, SearchableSelect } from '@galop/component-library';

import { GetOptionText, groupBy } from '../../Helpers/Helper';
import { makeCall } from '../../auth-utils';
import Can from '../Shared/Can';
import { DeleteButton } from '@galop/component-library';
import { useFormFields } from '../Shared/FormFields';
import { copyURL } from '../Inquiries/Embed';
import { useBSToasts } from '../Shared/BSToasts';

export const ViewProperty = (props) => {
    const ls = require('local-storage');
    const { config, IsDirty, resource, userId, Close, Save, NewResource, ReloadResources, resources } = props;
    const [srcUrl, setSRCUrl] = useState(null);
    const [urlCopied, setURLCopied] = useState(false);
    const [errors, addError, removeError] = useBSToasts([]);
    const { addToast, removeToast } = useToasts();

    /* Generic Variables */
    //const [resourceRecord, setResourceRecord] = useState(resource || {});
    const [resourceRecord, updateResource, setResourceRecord] = useFormFields(resource || {}, () => setResourceIsDirty(true));
    const [editResource, setEditResource] = useState(NewResource);
    const [resourceIsDirty, setResourceIsDirty] = useState(false);

    /* Resource Specific Variables */
    const [redirect, setRedirect] = useState('');
    const [editing, setEditing] = useState(false);

    useEffect(() => setSRCUrl(`${window.location.origin}?propertyIdentifier=${resourceRecord.propertyIdentifier}`), [resourceRecord.propertyIdentifier])

    useEffect(() => {
        IsDirty(resourceIsDirty || editing);
    }, [resourceIsDirty, editing]);

    useEffect(() => {
        if (resource) {
            setResourceRecord({ ...resource, emailAddresses: [...(resource.emailAddresses || []).filter(eml => eml.email), { id: Math.random(), propertyId: resource.id }] });
            if (('loaded' in resource) && !resource.loaded) {
                addToast(`Loading ${resource.name} data...`, { appearance: 'info', id: 'loading-data' });
            }
            else {
                removeToast('loading-data');
            }
        }
    }, [resource]);

    const deleteResource = async () => {
        makeCall(async (authHeader) => {
            addToast(`Deleting ${config.capitalResourceName}`, { appearance: 'info', id: 'deleting-resource' });
            await api.delete(`api/${config.controllerName}/${resourceRecord.id}`, {
                headers: authHeader
            }).catch((error) => globalErrorHandler(error, addToast));
            removeToast('deleting-resource', () => {
                setResourceIsDirty(false);
                if (Save) {
                    Save({ ...resourceRecord, deleted: !resource.deleted });
                }

                if (ReloadResources) {
                    ReloadResources();
                }

                Close();
            });
        });
    };

    const goToPage = (resource, to) => {
        if (resource) {
            ls(config.localStorage, resource);
        }
        setRedirect(to);
    };

    const resourceIsValid = () => {
        let isValid = true;
        isValid = isValid && (resourceRecord.name); /* Check for Name */
        isValid = isValid && (resourceRecord.propertyIdentifier); /* Check for Property Identifier */
        isValid = isValid && (resourceRecord.email); /* Check for Property Email Address */
        isValid = isValid && (resourceRecord.phone); /* Check for Property Phone Number */

        return isValid;
    };

    const saveResource = () => {
        if (resourceIsValid()) {
            makeCall(async (authHeader) => {
                (resourceRecord.emailAddresses || []).forEach(emailAddress => {
                    if (!isNaN(emailAddress.id)) {
                        delete emailAddress.id;
                    }
                });
                addToast(`Saving ${resourceRecord.name}`, { appearance: 'info', id: 'saving-resource' });
                if (resourceRecord.new) {
                    delete resourceRecord.id;
                }
                let url = `api/${config.controllerName}`;
                //if (resourceRecord.new) {
                //    url = `api/${config.controllerName}/${userId}`;
                //}
                const result = await api({
                    method: (resourceRecord.new ? 'post' : 'put'),
                    url: `${url}`,
                    data: { ...resourceRecord, emailAddresses: resourceRecord.emailAddresses.filter(eml => eml.email) },
                    headers: authHeader
                })
                    .catch(error => globalErrorHandler(error, addToast));

                if (result) {
                    setResourceIsDirty(false);
                    if (Save) {
                        if (resourceRecord.new && result.data) {
                            /* Resource was saved */
                            Save(result.data);
                        }
                        else {
                            Save(resourceRecord);
                        }
                    }
                    setEditResource(false);
                }

                removeToast('saving-resource');
            });
        }
        else {
            addToast('A required field is missing.', { appearance: 'error', autoDismiss: true });
        }
    };

    return (
        <div className="mb-2 d-flex flex-column h-100">
            {redirect && <Redirect to={redirect} />}
            <Prompt when={resourceIsDirty} message='You have unsaved changes, are you sure you want to leave?' />
            <div className="mb-1">
                <Row>
                    <Col className="d-flex">
                        <div className="d-inline-block d-md-none">
                            <Button color="link" className="py-0" onClick={Close}><FontAwesomeIcon icon={faChevronLeft} /></Button>
                        </div>
                        <div className="flex-fill d-flex">
                            <span className="h3 text-dark pb-0 mb-0">{resource.name}</span>
                        </div>
                    </Col>
                </Row>
            </div>
            <div className="flex-fill scrollable-list border-top border-bottom" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                {editResource ?
                    <Row className="my-2 my-sm-0">
                        <Col>
                            <Field type="text" label="Name" field="name" value={resourceRecord.name} fieldClassName={!resourceRecord.name ? 'is-invalid' : ''} onChange={updateResource} EnableInput={{ disabled: !editResource }} help={resourceRecord.pmSystem && resourceRecord.pmResourceIdentifier ? `${resourceRecord.pmSystem}: ${resourceRecord.pmResourceIdentifier}` : ''} />
                        </Col>
                        <Col>
                            <Field type="text" label="Property Identifier (yardi code)" field="propertyIdentifier" value={resourceRecord.propertyIdentifier} fieldClassName={!resourceRecord.propertyIdentifier ? 'is-invalid' : ''} onChange={updateResource} EnableInput={{ disabled: !editResource }} />
                        </Col>
                    </Row>
                    :
                    <div className="text-end fst-italic pe-1"><small>{resourceRecord.propertyIdentifier}</small></div>
                }
                <Row>
                    {!editResource &&
                        <Col xs="12">
                            <Label className="form-control-label">Embed URL</Label>
                            <InputGroup className="mb-0">
                                <div className="form-control bg-white">
                                    {srcUrl}
                                </div>
                                <Button color="secondary" onClick={() => copyURL({
                                    srcUrl,
                                    active: () => {
                                        setURLCopied(true);
                                        addError('Web Form URL copied!', { appearance: 'info', id: 'copied' });
                                    },
                                    inactive: () => {
                                        setURLCopied(false);
                                        removeError('copied');
                                    }
                                })}><FontAwesomeIcon icon={urlCopied ? faCheck : faCopy} /></Button>
                            </InputGroup>
                            <div class="d-block">
                                &nbsp;
                            </div>
                        </Col>
                    }
                    <Col>
                        <Field type="text" label="Email" field="email" value={resourceRecord.email} fieldClassName={!resourceRecord.email ? 'is-invalid' : ''} onChange={updateResource} EnableInput={{ disabled: !editResource }} />
                    </Col>
                    <Col>
                        <Field type="text" label="Phone" field="phone" value={resourceRecord.phone} fieldClassName={!resourceRecord.phone ? 'is-invalid' : ''} onChange={updateResource} EnableInput={{ disabled: !editResource }} />
                    </Col>
                </Row>
                {errors}
            </div>
            <Row className="pt-1">
                {!NewResource && (resourceRecord.deleted ?
                    <Can role={config.writeRole}
                        yes={() => <Col>
                            <DeleteButton color="danger" Close={deleteResource} ShowDropdown={true}>Delete</DeleteButton>
                        </Col>}
                    />
                    : <Can role={config.writeRole}
                        yes={() => <Col>
                            <DeleteButton color="danger" outline Close={deleteResource} ShowDropdown={true}>Delete</DeleteButton>
                        </Col>}
                    />)}
                {!editResource ?
                    <Col className="text-end">
                        <Button color="light" className="border me-1" onClick={Close}>Close</Button>
                        <Can role={config.writeRole}
                            yes={() => <Button color="secondary" onClick={() => setEditResource(true)}>Edit</Button>}
                        />
                    </Col>
                    :
                    <Col className="text-end">
                        <CancelButton color="light" className="border me-1" Close={resource && !resourceIsDirty && !resource.new ? () => {
                            setResourceRecord({ ...resource, emailAddresses: [...(resource.emailAddresses || []).filter(eml => eml.email), { id: Math.random(), propertyId: resource.id }] });
                            setResourceIsDirty(false);
                            setEditResource(false);
                        } : Close} Reload={() => {
                            setResourceRecord({ ...resource, emailAddresses: [...(resource.emailAddresses || []).filter(eml => eml.email), { id: Math.random(), propertyId: resource.id }] });
                            setResourceIsDirty(false);
                        }} ShowDropdown={resourceIsDirty}>Cancel</CancelButton>
                        <Button color="success" disabled={!resourceIsDirty} onClick={() => saveResource()}>Save</Button>
                    </Col>

                }
            </Row>
        </div>
    );
};


// WEBPACK FOOTER //
// ./src/components/Properties/ViewProperty.js